import type { ApRecurrencyFrequency } from '../generated/enums.js';

export interface BuildRecurringOccurrenceDatesInput {
  recurringSince: Date | null;
  recurringUntil: Date | null;
  totalInstallments: number | null;
  recurrencyFrequency: ApRecurrencyFrequency;
  planEffectiveFrom: Date;
  planEffectiveUntil: Date | null;
  includeRetroactivePeriods?: boolean;
  maxRetroactiveOccurrences?: number;
  maxForwardOccurrences?: number;
  maxIterations?: number;
  today?: Date;
}

const DEFAULT_MAX_OCCURRENCES = 24;
const DEFAULT_MAX_ITERATIONS = 2400;

export function toUtcDateOnly(value: Date): Date {
  return new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));
}

export function addUtcMonths(date: Date, months: number): Date {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  const firstOfTargetMonth = new Date(Date.UTC(year, month + months, 1));
  const lastDayOfTargetMonth = new Date(
    Date.UTC(firstOfTargetMonth.getUTCFullYear(), firstOfTargetMonth.getUTCMonth() + 1, 0),
  ).getUTCDate();
  firstOfTargetMonth.setUTCDate(Math.min(day, lastDayOfTargetMonth));
  return toUtcDateOnly(firstOfTargetMonth);
}

export function advanceRecurringDate(date: Date, recurrencyFrequency: ApRecurrencyFrequency): Date {
  if (recurrencyFrequency === 'WEEKLY') {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + 7);
    return toUtcDateOnly(next);
  }

  const monthsByFrequency: Record<Exclude<ApRecurrencyFrequency, 'WEEKLY'>, number> = {
    MONTHLY: 1,
    BIMONTHLY: 2,
    QUARTERLY: 3,
    SEMESTERLY: 6,
    YEARLY: 12,
  };

  return addUtcMonths(date, monthsByFrequency[recurrencyFrequency]);
}

export function isOnOrAfterUtcMonth(date: Date, reference: Date): boolean {
  const dateMonthStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  const referenceMonthStart = new Date(
    Date.UTC(reference.getUTCFullYear(), reference.getUTCMonth(), 1),
  );
  return dateMonthStart.getTime() >= referenceMonthStart.getTime();
}

export function buildRecurringOccurrenceDates(input: BuildRecurringOccurrenceDatesInput): Date[] {
  const today = toUtcDateOnly(input.today ?? new Date());
  const start = toUtcDateOnly(input.recurringSince ?? today);
  const until = toUtcDateOnly(input.recurringUntil ?? addUtcMonths(today, 12));
  const planEffectiveFrom = toUtcDateOnly(input.planEffectiveFrom);
  const planEffectiveUntil = input.planEffectiveUntil
    ? toUtcDateOnly(input.planEffectiveUntil)
    : null;

  const windowStart = start > planEffectiveFrom ? start : planEffectiveFrom;
  const windowEnd = planEffectiveUntil && planEffectiveUntil < until ? planEffectiveUntil : until;
  if (windowStart > windowEnd) {
    return [];
  }

  const maxInstallments = input.totalInstallments ?? Number.POSITIVE_INFINITY;
  const maxIterations = input.maxIterations ?? DEFAULT_MAX_ITERATIONS;
  const maxRetroactiveOccurrences = input.maxRetroactiveOccurrences ?? DEFAULT_MAX_OCCURRENCES;
  const maxForwardOccurrences = input.maxForwardOccurrences ?? DEFAULT_MAX_OCCURRENCES;

  let cursor = start;
  let installment = 0;
  let iterations = 0;
  let retroactiveCount = 0;
  let forwardCount = 0;
  const dates: Date[] = [];

  while (iterations < maxIterations && installment < maxInstallments && cursor <= windowEnd) {
    installment += 1;
    iterations += 1;

    if (cursor >= windowStart) {
      const isForwardDate = isOnOrAfterUtcMonth(cursor, today);
      if (isForwardDate) {
        if (forwardCount < maxForwardOccurrences) {
          dates.push(cursor);
          forwardCount += 1;
        }
      } else if (input.includeRetroactivePeriods && retroactiveCount < maxRetroactiveOccurrences) {
        dates.push(cursor);
        retroactiveCount += 1;
      }

      if (forwardCount >= maxForwardOccurrences) {
        break;
      }
    }

    const next = advanceRecurringDate(cursor, input.recurrencyFrequency);
    if (next.getTime() === cursor.getTime()) {
      break;
    }
    cursor = next;
  }

  return dates;
}
